// HtmlMedia.jsx
import React from "react";

const HtmlMedia = () => {
  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <h1>HTML Media</h1>

      {/* Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          HTML provides native support for embedding <strong>audio</strong> and <strong>video</strong> content using the <code>&lt;audio&gt;</code> and <code>&lt;video&gt;</code> elements. This allows seamless media playback without relying on plugins.
        </p>
      </section>

      {/* Audio Element */}
      <section id="audio">
        <h2>2. Audio</h2>
        <p>Basic syntax:</p>
        <pre>
          <code>{`<audio controls>
  <source src="audio-file.mp3" type="audio/mpeg">
  Your browser does not support the audio element.
</audio>`}</code>
        </pre>
        <audio controls style={{ marginTop: "10px" }}>
          <source src="https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3" type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>

        <h3>Attributes</h3>
        <ul>
          <li><code>controls</code> – Shows default play/pause controls</li>
          <li><code>autoplay</code> – Starts playing automatically</li>
          <li><code>loop</code> – Repeats media continuously</li>
          <li><code>muted</code> – Starts audio muted</li>
          <li><code>preload</code> – Specifies how the audio should be loaded (<em>auto, metadata, none</em>)</li>
        </ul>
      </section>

      {/* Video Element */}
      <section id="video">
        <h2>3. Video</h2>
        <p>Basic syntax:</p>
        <pre>
          <code>{`<video width="320" height="240" controls>
  <source src="video-file.mp4" type="video/mp4">
  Your browser does not support the video tag.
</video>`}</code>
        </pre>
        <video width="320" height="240" controls style={{ marginTop: "10px" }}>
          <source src="https://www.w3schools.com/html/mov_bbb.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        <h3>Attributes</h3>
        <ul>
          <li><code>controls</code> – Displays default video controls</li>
          <li><code>autoplay</code> – Plays automatically on page load</li>
          <li><code>loop</code> – Replays video indefinitely</li>
          <li><code>muted</code> – Video starts muted</li>
          <li><code>poster</code> – Image displayed before the video starts</li>
          <li><code>preload</code> – Determines loading strategy (<em>auto, metadata, none</em>)</li>
        </ul>
      </section>

      {/* Media Embedding */}
      <section id="embedding">
        <h2>4. Embedding Media</h2>
        <p>You can embed media from external sources like YouTube:</p>
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/dQw4w9WgXcQ"
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ marginTop: "10px" }}
        ></iframe>
      </section>

      {/* Media Events */}
      <section id="events">
        <h2>5. Media Events</h2>
        <ul>
          <li><code>onPlay</code> – Triggered when media starts playing</li>
          <li><code>onPause</code> – Triggered when media is paused</li>
          <li><code>onEnded</code> – Triggered when playback ends</li>
          <li><code>onTimeUpdate</code> – Triggered when playback position changes</li>
        </ul>
      </section>

      {/* Best Practices */}
      <section id="best-practices">
        <h2>6. Best Practices</h2>
        <ul>
          <li>Provide multiple formats for compatibility (<code>mp3, ogg</code> for audio; <code>mp4, WebM</code> for video).</li>
          <li>Use <code>controls</code> for user accessibility.</li>
          <li>Provide fallback content for unsupported browsers.</li>
          <li>Use <code>poster</code> for videos to display a preview image.</li>
          <li>Keep file sizes optimized for faster loading and better performance.</li>
        </ul>
      </section>
    </div>
  );
};

export default HtmlMedia;
